// ContinueStrip — signed-in-only resume bar shown between the hero and
// the PillarGrid. Surfaces the two most actionable "next steps":
//   • Resume the last playbook the user was running
//   • Jump to the next un-launched skill on the Mastery Path
//
// Renders nothing (returns null) when neither is available so guests
// and brand-new users get a clean, hero-only home page.
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Compass } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePlaybookProgress } from "@/hooks/usePlaybookProgress";
import { useMasteryJourney } from "@/hooks/useMasteryJourney";
import { AI_MASTERY_PATH } from "@/data/aiMasteryPath";
import { PLAYBOOKS } from "@/data/playbooks";

const ContinueStrip = () => {
  const { user } = useAuth();
  const { progress } = usePlaybookProgress();
  const { journey } = useMasteryJourney();

  if (!user) return null;

  const resumePlaybook = progress
    ? PLAYBOOKS.find((p) => p.slug === progress.slug)
    : null;

  // Next mastery skill = first one the user hasn't launched yet.
  const nextSkill = AI_MASTERY_PATH.find(
    (s) => !journey.launchedSkills.includes(s.label),
  );

  if (!resumePlaybook && !nextSkill) return null;

  return (
    <section
      aria-label="Continue your journey"
      className="container mx-auto px-4 pt-2"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {resumePlaybook ? (
          <Link
            to={`/?playbook=${encodeURIComponent(resumePlaybook.slug)}&resume=1`}
            className="group flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors p-3"
          >
            <PlayCircle className="w-5 h-5 text-primary shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-primary/80">
                Resume playbook
              </p>
              <p className="text-sm font-mono font-semibold text-foreground truncate">
                {resumePlaybook.title}
              </p>
            </div>
            <ArrowRight
              className="w-4 h-4 text-primary/60 group-hover:translate-x-0.5 transition-transform shrink-0"
              aria-hidden
            />
          </Link>
        ) : null}
        {nextSkill ? (
          <Link
            to={`/?skill=${encodeURIComponent(nextSkill.label)}`}
            className="group flex items-center gap-3 rounded-lg border border-border bg-card/40 hover:bg-card transition-colors p-3"
          >
            <Compass className="w-5 h-5 text-foreground shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Next on the roadmap
              </p>
              <p className="text-sm font-mono font-semibold text-foreground truncate">
                {nextSkill.label}
              </p>
            </div>
            <ArrowRight
              className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform shrink-0"
              aria-hidden
            />
          </Link>
        ) : null}
      </div>
    </section>
  );
};

export default ContinueStrip;